import { useCallback, useEffect, useRef, useState } from 'react'
import DebugScreen from './DebugScreen'
import { createStage } from './stage'
import { listLogs, exportLogs } from './logFiles'

const LONG_PRESS_MS = 700

function nextLabel(label) {
    const m = label.match(/^(.*?)(\d+)$/)
    if (!m) return `${label}-02`
    return m[1] + String(Number(m[2]) + 1).padStart(m[2].length, '0')
}

// The Gate 1 shell: a blank screen until the startup path finishes, then the
// war screen (PixiJS stage). The debug screen sits over it when opened.
// Nothing here re-renders while a run records.
export default function Gate1App({ app }) {
    const [startupDone, setStartupDone] = useState(false)
    const [debugOpen, setDebugOpen] = useState(false)
    const [pendingRun, setPendingRun] = useState(null)
    const [runActive, setRunActive] = useState(false)
    const [lastResult, setLastResult] = useState(null)
    const [label, setLabel] = useState('run-01')
    const [crash, setCrash] = useState(null)
    const [logs, setLogs] = useState([])
    const [exportStatus, setExportStatus] = useState(null)
    const [sceneStatus, setSceneStatus] = useState(null)
    const hostRef = useRef(null)
    const pressTimer = useRef(null)

    useEffect(() => { app.startupDone.then(() => setStartupDone(true)) }, [app])

    useEffect(() => {
        if (!startupDone || !hostRef.current) return
        let stage = null
        let cancelled = false
        app.initialFixture
            .then(fixture => createStage(hostRef.current, {
                onFrame: app.controller.frameTick, onFirstRender: app.onFirstWarFrame, fixture,
                topUp: !!fixture && app.isGraded(fixture.meta.name), isRecording: app.isRecording,
            }))
            .then(s => {
            if (cancelled) { s.destroy(); return }
            stage = s
            app.attachStage(s)
        })
        return () => { cancelled = true; stage?.destroy() }
    }, [startupDone, app])

    // A run ends: the app has written its log; show the outcome next time the
    // debug screen opens.
    useEffect(() => {
        app.onRunWritten = (summary) => {
            setRunActive(false)
            setLastResult(summary)
        }
        return () => { app.onRunWritten = null }
    }, [app])

    const openDebug = useCallback(() => {
        setDebugOpen(true)
        app.crashSnapshot().then(setCrash)
        listLogs().then(setLogs)
    }, [app])

    // Web-only automation (?autorun=label) for desktop verification.
    useEffect(() => {
        if (!startupDone || !app.autorun) return
        const t = setInterval(() => {
            if (!app.ready()) return
            clearInterval(t)
            setRunActive(true)
            setPendingRun(app.autorun)
        }, 250)
        return () => clearInterval(t)
    }, [startupDone, app])

    // Arm only after the commit that removed the debug screen from the DOM.
    useEffect(() => {
        if (!pendingRun) return
        if (document.querySelector('[data-gate1-debug]')) throw new Error('debug screen still mounted at arm time')
        app.armRun(pendingRun, { debugUnmountedAtPerfMs: performance.now() })
        setPendingRun(null)
    }, [pendingRun, app])

    const startRun = (cfg) => {
        if (!app.ready()) { setLastResult('War screen not ready yet'); return }
        setLabel(nextLabel(cfg.label))
        setLastResult(null)
        setRunActive(true)
        setDebugOpen(false)
        setPendingRun(cfg)
    }

    const sceneAction = async (action, arg) => {
        setSceneStatus('Working…')
        try {
            if (action === 'show') { await app.showFixture(arg); setSceneStatus(`Showing ${arg}`) }
            else if (action === 'beat') { app.setBeatKind(arg); setSceneStatus(`Beat: ${arg}`) }
            else if (action === 'verify') setSceneStatus(await app.verifyFixtures())
            else if (action === 'screenshot') setSceneStatus(await app.saveScreenshot())
            else if (action === 'contextLoss') setSceneStatus(await app.testContextLoss())
            listLogs().then(setLogs)
        } catch (e) {
            setSceneStatus(`Failed: ${e.message ?? e}`)
        }
    }

    const doExport = async () => {
        setExportStatus('Exporting…')
        try {
            const r = await exportLogs(await app.exportExtras())
            setExportStatus(`Shared ${r.count} file(s)`)
        } catch (e) {
            setExportStatus(`Export failed: ${e.message ?? e}`)
        }
    }

    const pressStart = () => {
        clearTimeout(pressTimer.current)
        pressTimer.current = setTimeout(openDebug, LONG_PRESS_MS)
    }
    const pressEnd = () => clearTimeout(pressTimer.current)

    return (
        <div className="gate1-root">
            <div className="gate1-stage" ref={hostRef} />
            {startupDone && !runActive && !debugOpen && (
                <div
                    className="gate1-corner"
                    onPointerDown={pressStart}
                    onPointerUp={pressEnd}
                    onPointerLeave={pressEnd}
                    onPointerCancel={pressEnd}
                    onContextMenu={e => e.preventDefault()}
                />
            )}
            {debugOpen && !runActive && (
                <DebugScreen
                    build={app.build}
                    deviceInfo={app.deviceInfo()}
                    crash={crash}
                    startup={app.startupRecord()}
                    lastResult={lastResult}
                    logs={logs}
                    defaultLabel={label}
                    onStart={startRun}
                    onExport={doExport}
                    onClose={() => setDebugOpen(false)}
                    exportStatus={exportStatus}
                    scene={{ names: app.fixtureNames, current: app.sceneName(), beat: app.beatKind() }}
                    onSceneAction={sceneAction}
                    sceneStatus={sceneStatus}
                />
            )}
        </div>
    )
}
